/**
 * §1 "Baseline 2025 survey" — a filter bar and one full chart per survey series.
 *
 * The survey is 58 `mb_*` series in ten workbook sheets, and the whole of it arrives on the
 * same `/dashboard` payload the rest of the page is drawn from: this section fetches
 * nothing, exactly like `DashboardCards.tsx`, and every number it prints is the server's.
 *
 * **What changed on 2026-09-16, and why.** The section used to be ten cards of four compact
 * rows, each row a number and a twelve-pixel four-country strip, with a "Show all" behind
 * which the other 18 series hid. The client's direction was *"be strategic with the way we
 * present the baseline information: sections per sheet, each sub-indicator with its own full
 * chart, mixed chart types, all responding to filters, clear not compact"*, and every one of
 * those words is an argument against the old layout: a strip is not a chart, a hidden series
 * is not presented at all, and one row per sub-indicator is the definition of compact. So:
 *
 * * **A filter instead of a truncation** (`BaselineFilter`). A reader gets all 58 series,
 *   not the first four of each sheet, and narrows by sheet or by name rather than by
 *   expanding ten cards one at a time.
 * * **A subsection per sheet**, numbered off the page's own section numeral — III.1, III.2 —
 *   so the survey reads as part of the document rather than as a grid of widgets.
 * * **One card per chart, and four kinds of chart** (`BaselineCharts`), chosen from the
 *   shape of the data rather than by hand: see `chartPlan` for the rule.
 * * **The filter composes with the page's scope** rather than replacing it (`baselineAxis`).
 *   The country and watershed selects at the top of the dashboard still decide *whose*
 *   figures these are; this bar decides how they are laid out.
 *
 * Two things deliberately survived the rebuild:
 *
 * * **A fallback is said out loud, once.** No region, commune or partnership was surveyed,
 *   so the server answers those scopes with the unit's *country*. A page that silently
 *   showed Burkina Faso's figures while its own heading said "Yaadga" would be a lie in the
 *   quietest possible form — the note under the section title names both geographies, and it
 *   is a fact about the whole answer, so it is printed there rather than on each subsection.
 * * **"View on map"** hands the sheet's own theme key up to the page (`bl_<group_id>`), the
 *   way the catalogue's preview does: a theme is not a permalink key, so the store is
 *   written and the route changed — see `features/catalogue/preview.ts` for the full
 *   reasoning.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DashboardBaseline, DashboardBaselineSheet } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { BaselineChartCard } from './BaselineCharts';
import { BaselineFilter } from './BaselineFilter';
import { baselineAxis, chartPlan, filterSheets, type BaselineView } from './baseline';
import { sectionSpec } from './dashboard';

/**
 * "III" — read off the one ordered section spec rather than passed in.
 *
 * The subsection headers are numbered *within* the section (III.1, III.2), which needs the
 * section's own numeral; taking it as a prop would have meant a fourth prop on a component
 * two other files render, for a string that is already written down once and cannot differ.
 */
const SECTION_NUMERAL = sectionSpec.find((s) => s.key === 'baseline')?.numeral ?? 'III';

export interface BaselineSectionProps {
  baseline: DashboardBaseline;
  /**
   * The unit the *page* is scoped to, when it is one the survey could not answer for — the
   * name the fallback note owes the reader. `null` at any scope the survey covers.
   */
  unitLabel?: string | null;
  onMap: (groupId: string) => void;
  /**
   * §1 — `false` when the block sits under a numbered section header that already prints
   * "Baseline 2025" (the dashboard page). The fallback note is *not* the title and stays
   * either way: it says what the figures below are an estimate for, which the section
   * header deliberately does not.
   */
  showTitle?: boolean;
}

export function BaselineSection({
  baseline,
  unitLabel,
  onMap,
  showTitle = true,
}: BaselineSectionProps) {
  const { t } = useTranslation();
  const [sheetId, setSheetId] = useState('all');
  const [view, setView] = useState<BaselineView>('countries');
  const [query, setQuery] = useState('');

  const sheets = useMemo(
    () => filterSheets(baseline.sheets, sheetId, query),
    [baseline.sheets, sheetId, query],
  );
  /*
    One axis for the whole section, not one per chart: forty charts on one screen must put
    Mali in the same place, and recomputing the same four rows forty times is work that buys
    nothing. It changes only when the view or the page's scope does.
  */
  const rows = useMemo(() => baselineAxis(baseline, view), [baseline, view]);
  /** The sheet's number within the section, from the *unfiltered* list — III.4 stays III.4. */
  const numeralOf = useMemo(() => {
    const index = new Map(baseline.sheets.map((s, i) => [s.group_id, `${SECTION_NUMERAL}.${i + 1}`]));
    return (groupId: string) => index.get(groupId) ?? SECTION_NUMERAL;
  }, [baseline.sheets]);

  if (baseline.sheets.length === 0) return null;

  // The note names the country the figures *are* from and the unit they are *not* from.
  const fallback =
    baseline.fallback && unitLabel
      ? t('dashboard.baseline.fallback', { country: baseline.scope_label, unit: unitLabel })
      : null;

  return (
    <section className="dash-baseline" data-testid="dash-baseline">
      <div className="dash-baseline-head">
        {showTitle ? (
          <h3 className="dash-baseline-title">{t('dashboard.baseline.title')}</h3>
        ) : null}
        {/* Once, under the section's own title — not ten times down the page. The note is
            about the whole answer, and a card is not where the scope is decided (review,
            fix round 1). */}
        {fallback ? (
          <p className="dash-baseline-fallback" data-testid="baseline-fallback">
            {fallback}
          </p>
        ) : null}
      </div>

      <BaselineFilter
        sheets={baseline.sheets}
        sheetId={sheetId}
        onSheet={setSheetId}
        view={view}
        onView={setView}
        query={query}
        onQuery={setQuery}
      />

      {sheets.length === 0 ? (
        <p className="bl-noresults" data-testid="baseline-no-results">
          {t('dashboard.baseline.filter.empty', { q: query })}
        </p>
      ) : (
        sheets.map((sheet) => (
          <SheetBlock
            key={sheet.group_id}
            sheet={sheet}
            numeral={numeralOf(sheet.group_id)}
            baseline={baseline}
            rows={rows}
            view={view}
            onMap={onMap}
          />
        ))
      )}
    </section>
  );
}

/**
 * One workbook sheet: a numbered header, and its series as full charts, two to a row.
 *
 * Two per row above 900 px and one below is the widest a chart can be and still put four
 * country rows on a 280-pixel body; three would take the bars back down to the strip widths
 * this rebuild exists to get rid of.
 */
function SheetBlock({
  sheet,
  numeral,
  baseline,
  rows,
  view,
  onMap,
}: {
  sheet: DashboardBaselineSheet;
  numeral: string;
  baseline: DashboardBaseline;
  rows: ReturnType<typeof baselineAxis>;
  view: BaselineView;
  onMap: (groupId: string) => void;
}) {
  const { t } = useTranslation();
  const specs = useMemo(
    () => chartPlan(sheet.series, `${sheet.group_id}-`),
    [sheet.series, sheet.group_id],
  );

  return (
    <div className="bl-sheet" data-testid={`baseline-sheet-${sheet.group_id}`}>
      <div className="bl-sheet-head">
        <div className="bl-sheet-text">
          <h4 className="bl-sheet-title">
            <span className="bl-sheet-num" aria-hidden="true">
              {numeral}
            </span>
            {sheet.label}
          </h4>
        </div>
        <button
          type="button"
          className="btn ghost sm"
          data-testid={`baseline-map-${sheet.group_id}`}
          onClick={() => onMap(sheet.group_id)}
        >
          <Icon name="map" size={13} />
          {t('dashboard.baseline.map')}
        </button>
      </div>
      <div className="bl-sheet-grid">
        {specs.map((spec) => (
          <BaselineChartCard
            key={spec.id}
            spec={spec}
            baseline={baseline}
            rows={rows}
            view={view}
            sheetLabel={sheet.label}
          />
        ))}
      </div>
    </div>
  );
}

export default BaselineSection;
